import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  getAllUploadingFiles,
  getMediaAssetsMap,
} from '../../../../redux/files/files.selectors';
import { StoreState } from '../../../../redux/reducers/store.types';
import { MediaAsset, UploadingFile } from '../../../../types/media-assets';
import { MaybeUndef, StringMap } from '../../../../types/system';
import { OperationGroupsState } from '../operation-groups.reducer';
import { getOperationGroupsState } from '../operation-groups.selectors';
import {
  FileUploadOperation,
  FileUploadStatus,
  FileUploadStatusType,
  OperationGroupType,
  UploadOperationGroup,
} from '../operation-groups.types';
import { UploadOperationsState } from '../upload-operations/upload-operations.reducer';
import { getOperationsState } from '../upload-operations/upload-operations.selectors';
import { OwnProps } from './upload-group.container';

const getGroupIdForUploadGroupComponent = (
  state: StoreState,
  { groupId }: OwnProps,
) => groupId;

export const getGroupForUploadGroupComponent = createSelector(
  getGroupIdForUploadGroupComponent,
  getOperationGroupsState,
  (
    groupId: string,
    operationGroupsState: OperationGroupsState,
  ): UploadOperationGroup => {
    const opGroup = operationGroupsState.groupsMap[groupId];

    if (!opGroup || opGroup.type !== OperationGroupType.Upload) {
      throw new Error('Upload operation group was not found');
    }

    return opGroup;
  },
);

export const getAllUploadingFilesMap = createSelector(
  getAllUploadingFiles,
  (allUploadingFiles: UploadingFile[]): StringMap<UploadingFile> => {
    return _.keyBy(allUploadingFiles, 'id');
  },
);

export const getFileUploadOperationsForGroup = createSelector(
  getGroupForUploadGroupComponent,
  getOperationsState,
  getAllUploadingFilesMap,
  getMediaAssetsMap,
  (
    opGroup: UploadOperationGroup,
    operationsState: UploadOperationsState,
    allUploadingFilesMap: StringMap<UploadingFile>,
    mediaAssetsMap: StringMap<MediaAsset>,
  ): FileUploadOperation[] => {
    return opGroup.fileClientIdsList.map((clientId: string) => {
      const op: MaybeUndef<FileUploadStatus> = operationsState[clientId];

      if (!op) {
        throw new Error('Wrong operation type');
      }

      switch (op.type) {
        case FileUploadStatusType.Uploading: {
          const file: MaybeUndef<UploadingFile> =
            allUploadingFilesMap[op.clientId];
          return {
            id: op.clientId,
            name: file ? file.name : '--',
            size: file ? file.size : 0,
            progress: file ? file.progress : 0,
            status: op,
          };
        }

        case FileUploadStatusType.Uploaded: {
          const file = mediaAssetsMap[op.fileId];
          return {
            id: op.clientId,
            name: file ? file.clientName : '--',
            size: file ? file.size : 0,
            status: op,
          };
        }

        case FileUploadStatusType.Failed:
          return {
            id: op.clientId,
            name: op.file.name,
            size: op.file.size,
            status: op,
          };

        case FileUploadStatusType.Canceled:
          return {
            id: op.clientId,
            name: op.file.name,
            size: op.file.size,
            status: op,
          };

        default:
          throw new Error('Unsupported type File Upload status');
      }
    });
  },
);
